"use client";

import React, {
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";

import Button from "@mui/material/Button";
import InputBase from "@mui/material/InputBase";
import Stack from "@mui/material/Stack";
import {alpha, useTheme} from "@mui/material/styles";

import FaIcon from "@/components/utilities/fa-icon";
import {PaginationProps} from "@/types/page-response";

export default function Pagination({
                                       numberOfPages = 1,
                                       changePageHandler,
                                       currentPage = 1,
                                   }: PaginationProps): JSX.Element | null {
    const theme = useTheme();

    const [inputText, setInputText] = useState(
        currentPage.toString()
    );

    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(
        null
    );

    const shouldRender = numberOfPages > 1;

    const normalizePage = useCallback(
        (value: string | number): number => {
            const parsedPage = Number(value);

            if (!Number.isFinite(parsedPage)) {
                return currentPage;
            }

            return Math.min(
                Math.max(Math.trunc(parsedPage), 1),
                numberOfPages
            );
        },
        [currentPage, numberOfPages]
    );

    const goToPage = useCallback(
        (page: number) => {
            const normalizedPage = normalizePage(page);

            setInputText(normalizedPage.toString());

            if (normalizedPage === currentPage) {
                return;
            }

            changePageHandler(normalizedPage);

            window.scrollTo({
                top: 0,
                behavior: "smooth",
            });
        },
        [
            changePageHandler,
            currentPage,
            normalizePage,
        ]
    );

    const applyInputValue = useCallback(() => {
        if (inputText.trim() === "") {
            setInputText(currentPage.toString());
            return;
        }

        goToPage(normalizePage(inputText));
    }, [
        currentPage,
        goToPage,
        inputText,
        normalizePage,
    ]);

    useEffect(() => {
        setInputText(currentPage.toString());
    }, [currentPage]);

    useEffect(() => {
        if (!shouldRender || inputText.trim() === "") {
            return;
        }

        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }

        const parsedPage = Number(inputText);

        if (
            !Number.isFinite(parsedPage) ||
            Math.trunc(parsedPage) === currentPage
        ) {
            return;
        }

        timerRef.current = setTimeout(() => {
            goToPage(normalizePage(inputText));
        }, 800);

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, [
        currentPage,
        goToPage,
        inputText,
        normalizePage,
        shouldRender,
    ]);

    const handleSubmit = (
        event: React.FormEvent<HTMLFormElement>
    ) => {
        event.preventDefault();

        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }

        applyInputValue();
    };

    if (!shouldRender) {
        return null;
    }

    return (
        <form onSubmit={handleSubmit}>
            <Stack
                pt={5}
                alignItems="center"
                justifyContent="center"
                direction={{xs: "column", md: "row"}}
                spacing={1}
            >
                <Stack
                    spacing={1}
                    direction="row-reverse"
                    sx={{
                        "& .MuiButton-root": {
                            minWidth: 50,
                            height: 50,
                        },
                    }}
                >
                    <Button
                        type="button"
                        disabled={currentPage <= 1}
                        color="primary"
                        variant="outlined"
                        onClick={() => goToPage(1)}
                        sx={{
                            fontSize: 18,
                        }}
                    >
                        1
                    </Button>

                    <Button
                        type="button"
                        disabled={currentPage <= 1}
                        color="primary"
                        variant="outlined"
                        onClick={() => goToPage(currentPage - 1)}
                    >
                        <FaIcon faIcon="fa-arrow-left"/>
                    </Button>

                    <InputBase
                        dir="ltr"
                        type="number"
                        value={inputText}
                        inputProps={{
                            inputMode: "numeric",
                            "aria-label": "شماره صفحه",
                            sx: {
                                px: 1,
                                textAlign: "center",
                                fontWeight: 400,
                            },
                        }}
                        onChange={(event) => {
                            setInputText(event.target.value);
                        }}
                        onFocus={() => {
                            if (inputText === currentPage.toString()) {
                                setInputText("");
                            }
                        }}
                        onBlur={applyInputValue}
                        sx={{
                            bgcolor: alpha(
                                theme.palette.primary.main,
                                0.1
                            ),
                            border: `1px solid ${theme.palette.primary.main}`,
                            borderRadius: theme.shape.borderRadius,
                            fontSize: 16,
                            color: `${theme.palette.primary.main} !important`,
                            fontWeight: 500,
                            height: 50,
                            width: 70,
                            minWidth: 50,
                            flexShrink: 0,
                            boxSizing: "border-box",
                        }}
                    />

                    <Button
                        type="button"
                        color="primary"
                        variant="outlined"
                        disabled={currentPage >= numberOfPages}
                        onClick={() => goToPage(currentPage + 1)}
                    >
                        <FaIcon faIcon="fa-arrow-right"/>
                    </Button>

                    <Button
                        type="button"
                        disabled={currentPage >= numberOfPages}
                        color="primary"
                        variant="outlined"
                        onClick={() => goToPage(numberOfPages)}
                        sx={{
                            fontSize: 18,
                        }}
                    >
                        {numberOfPages}
                    </Button>
                </Stack>
            </Stack>
        </form>
    );
}
